import {AxiosResponse} from 'axios'
import {HttpInstance} from '../createHttpInstance'

/**
 * The rotated TokenID comes back as a `Secure`, `HttpOnly` cookie
 * (`RefreshHTTPRespond` → `jwt.AddTokenInResponse`), so the body is
 * deliberately emptied before it is written.
 *
 * **The body carries no status.** The wire response is literally `{"Token":""}`:
 * `auth`'s contract type has exactly one field (`gen/pb/Auth_contract.pb.go` →
 * `RefreshResponse{Token string}`), and `server.go`'s `RefreshHTTPRespond`
 * moves the token into the cookie and then encodes a *zero-valued*
 * `RefreshResponse` as the body. There is no `Status` field anywhere in the
 * auth contract.
 *
 * This shape was previously typed as `{Status: 'Failure' | 'Success'}`, which no
 * server ever sent. The domain layer required `Status === 'Success'`, so every
 * successful refresh was read as a failure and fell through to the Auth0
 * re-exchange — the full-page redirect that destroys an in-flight photo upload.
 * QA caught it on AB#24880 after the verb fix (47264745) had already landed:
 * the request was correct by then, and the *response* was still being
 * misread. Success is signalled by the HTTP status, not by the body.
 */
export interface RawRefreshResponse {
  Token: string
}
export interface Refresh<R> {
  (http: HttpInstance): Promise<AxiosResponse<R>>
}

/**
 * Slides the legacy admin session forward (AB#24880).
 *
 * The endpoint already exists server-side: it authenticates without expiration
 * and rotates the browser's TokenID, which is what actually extends the session
 * the browser presents — unlike an Auth0 re-exchange, which mints a new token
 * family while leaving the presented session row on its original expiry.
 *
 * **GET, not POST.** The route is registered GET-only in the `auth` service
 * (`gen/http.go` → `rRefresh.Methods("GET")`), and its generated Go client
 * issues a GET too. A POST does not come back 405: gorilla/mux matches no route
 * and returns **404**, so the session silently never slides and every scheduled
 * refresh falls through to the Auth0 re-exchange fallback — which ends in a
 * full-page redirect that destroys an in-flight photo upload. That is exactly
 * what shipped and what QA caught on AB#24880; the original work item text
 * specified POST, so don't "restore" it.
 *
 * No body. The cookie rides on `withCredentials: true`, which is already the
 * default on the shared axios instance.
 */
export const refresh: Refresh<RawRefreshResponse> = (http) =>
  http.get('/v1/auth/refresh')
